/**
 * Pre-Audit List Page
 *
 * Shows all pre-audit assessments for consultant's clients.
 * Consultants can view, create, and manage pre-audit checklists.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Search, Filter, Plus, CheckCircle2, Clock, XCircle, FileText } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { ProgressBar } from '../../components/common/ProgressBar';
import { formatDate } from '../../utils/formatting';

interface PreAudit {
  id: string;
  clientId: string;
  clientName: string;
  applicationId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  progress: number;
  scheduledDate?: string;
  completedDate?: string;
  consultant: string;
  findings?: {
    total: number;
    passed: number;
    failed: number;
    notApplicable: number;
  };
}

export const PreAuditListPage: React.FC = () => {
  const { t } = useTranslation(['common', 'consultant']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [preAudits, setPreAudits] = useState<PreAudit[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setPreAudits([
        {
          id: 'pa-001',
          clientId: 'client-001',
          clientName: 'ABC Food Manufacturing Sdn Bhd',
          applicationId: 'app-001',
          status: 'completed',
          progress: 100,
          scheduledDate: '2025-12-15',
          completedDate: '2025-12-20',
          consultant: 'Ahmad Rahman',
          findings: {
            total: 45,
            passed: 42,
            failed: 2,
            notApplicable: 1,
          },
        },
        {
          id: 'pa-002',
          clientId: 'client-002',
          clientName: 'Global Ingredients Supply Co.',
          applicationId: 'app-002',
          status: 'in_progress',
          progress: 65,
          scheduledDate: '2025-12-28',
          consultant: 'Ahmad Rahman',
          findings: {
            total: 45,
            passed: 28,
            failed: 1,
            notApplicable: 0,
          },
        },
        {
          id: 'pa-003',
          clientId: 'client-003',
          clientName: 'Premium Bakery Industries',
          applicationId: 'app-003',
          status: 'pending',
          progress: 0,
          scheduledDate: '2026-01-05',
          consultant: 'Ahmad Rahman',
        },
        {
          id: 'pa-004',
          clientId: 'client-004',
          clientName: 'Tasty Snacks Manufacturing',
          applicationId: 'app-004',
          status: 'in_progress',
          progress: 30,
          scheduledDate: '2025-12-30',
          consultant: 'Ahmad Rahman',
          findings: {
            total: 45,
            passed: 12,
            failed: 1,
            notApplicable: 0,
          },
        },
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getStatusBadge = (status: PreAudit['status']) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success" dot>{t('consultant:preAudit.status.completed')}</Badge>;
      case 'in_progress':
        return <Badge variant="warning" dot>{t('consultant:preAudit.status.inProgress')}</Badge>;
      case 'pending':
        return <Badge variant="info" dot>{t('consultant:preAudit.status.pending')}</Badge>;
      case 'failed':
        return <Badge variant="error" dot>{t('consultant:preAudit.status.failed')}</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: PreAudit['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-success-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-warning-600" />;
      case 'pending':
        return <FileText className="w-5 h-5 text-info-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-error-600" />;
    }
  };

  const filteredPreAudits = preAudits.filter((audit) => {
    const matchesSearch = audit.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         audit.applicationId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || audit.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <PageLayout
        pageTitle={t('consultant:preAudit.title')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('consultant:preAudit.title') },
        ]}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={t('consultant:preAudit.title')}
      actions={
        <Button
          variant="primary"
          icon={<Plus className="w-4 h-4" />}
          onClick={() => navigate('/consultant/clients')}
        >
          {t('consultant:preAudit.newPreAudit')}
        </Button>
      }
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('consultant:preAudit.title') },
      ]}
    >
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('consultant:preAudit.stats.total')}</p>
                <p className="text-2xl font-bold text-gray-900">{preAudits.length}</p>
              </div>
              <ClipboardList className="w-8 h-8 text-primary-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('consultant:preAudit.stats.completed')}</p>
                <p className="text-2xl font-bold text-success-600">
                  {preAudits.filter(a => a.status === 'completed').length}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-success-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('consultant:preAudit.stats.inProgress')}</p>
                <p className="text-2xl font-bold text-warning-600">
                  {preAudits.filter(a => a.status === 'in_progress').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-warning-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('consultant:preAudit.stats.pending')}</p>
                <p className="text-2xl font-bold text-info-600">
                  {preAudits.filter(a => a.status === 'pending').length}
                </p>
              </div>
              <FileText className="w-8 h-8 text-info-600" />
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('consultant:preAudit.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">{t('consultant:preAudit.filters.all')}</option>
                <option value="pending">{t('consultant:preAudit.filters.pending')}</option>
                <option value="in_progress">{t('consultant:preAudit.filters.inProgress')}</option>
                <option value="completed">{t('consultant:preAudit.filters.completed')}</option>
                <option value="failed">{t('consultant:preAudit.filters.failed')}</option>
              </select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Pre-Audit List */}
      {filteredPreAudits.length === 0 ? (
        <EmptyState
          icon={<ClipboardList className="w-12 h-12" />}
          title={t('consultant:preAudit.emptyState.title')}
          description={t('consultant:preAudit.emptyState.description')}
          action={{
            label: t('consultant:preAudit.emptyState.action'),
            onClick: () => navigate('/consultant/clients'),
            icon: <Plus className="w-4 h-4" />,
          }}
        />
      ) : (
        <div className="space-y-4">
          {filteredPreAudits.map((audit) => (
            <Card
              key={audit.id}
              hover
              className="cursor-pointer transition-shadow"
              onClick={() => navigate(`/consultant/pre-audit/${audit.id}`)}
            >
              <CardBody>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Status Icon */}
                    <div className="mt-1">
                      {getStatusIcon(audit.status)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {audit.clientName}
                        </h3>
                        {getStatusBadge(audit.status)}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-3">
                        <div>
                          <span className="font-medium">{t('consultant:preAudit.applicationId')}:</span>{' '}
                          {audit.applicationId}
                        </div>
                        <div>
                          <span className="font-medium">{t('consultant:preAudit.scheduledDate')}:</span>{' '}
                          {audit.scheduledDate ? formatDate(audit.scheduledDate) : '-'}
                        </div>
                        {audit.completedDate && (
                          <div>
                            <span className="font-medium">{t('consultant:preAudit.completedDate')}:</span>{' '}
                            {formatDate(audit.completedDate)}
                          </div>
                        )}
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">{t('consultant:preAudit.progress')}</span>
                          <span className="font-medium text-gray-900">{audit.progress}%</span>
                        </div>
                        <ProgressBar
                          value={audit.progress}
                          max={100}
                          variant={
                            audit.progress === 100
                              ? 'success'
                              : audit.progress > 0
                              ? 'warning'
                              : 'default'
                          }
                          size="md"
                          showLabel={false}
                        />
                      </div>

                      {/* Findings Summary */}
                      {audit.findings && (
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-600">
                            {t('consultant:preAudit.findings')}:
                          </span>
                          <span className="text-success-600 font-medium">
                            ✓ {audit.findings.passed}
                          </span>
                          <span className="text-error-600 font-medium">
                            ✗ {audit.findings.failed}
                          </span>
                          <span className="text-gray-500">
                            N/A {audit.findings.notApplicable}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/consultant/pre-audit/${audit.id}`);
                    }}
                  >
                    {audit.status === 'completed'
                      ? t('consultant:preAudit.viewReport')
                      : t('consultant:preAudit.continue')}
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </PageLayout>
  );
};

